import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useSuspenseQuery } from "@tanstack/react-query";
import {
  ArrowRight, Check, Search, MapPin, Wallet, MessageSquare,
  Heart, UserRound,
  Utensils, Home as HomeIcon, ShieldCheck, MessageCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CuisineComboboxSingle } from "@/components/cuisine-combobox";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ChefCard } from "@/components/chef-card";
import { LaunchCounter } from "@/components/launch-counter";
import { ChefMapLazy } from "@/components/chef-map-lazy";
import { withCoords } from "@/components/chef-map-utils";
import { chefQueries, type PublicChef } from "@/lib/chefs.queries";
import heroImg from "@/assets/hero-chef.jpg";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "ChefConnect — Trova il tuo chef privato in Italia" },
      { name: "description", content: "Personal chef, chef a domicilio, show cooking e cene private in tutta Italia. Profili verificati, contatto diretto con lo chef." },
      { property: "og:title", content: "ChefConnect — Trova il tuo chef privato" },
      { property: "og:description", content: "Personal chef e cene private in tutta Italia. Profili verificati." },
      { property: "og:url", content: "https://chefconnectitalia.com/" },
      { property: "og:type", content: "website" },
      { property: "og:image", content: "https://chefconnectitalia.com/__l5e/assets-v1/a2ae4b98-4144-41f7-845e-72f224ca696b/og-home.jpg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "640" },
      { name: "twitter:image", content: "https://chefconnectitalia.com/__l5e/assets-v1/a2ae4b98-4144-41f7-845e-72f224ca696b/og-home.jpg" },
    ],
    links: [{ rel: "canonical", href: "https://chefconnectitalia.com/" }],
  }),
  loader: ({ context }) => context.queryClient.ensureQueryData(chefQueries.list(60)),
  errorComponent: ({ error }) => (
    <div className="container mx-auto max-w-2xl px-4 py-24 text-center">
      <h1 className="font-display text-3xl">Impossibile caricare la home</h1>
      <p className="mt-3 text-sm text-muted-foreground">{error.message}</p>
    </div>
  ),
  component: HomePage,
});

function HomePage() {
  const navigate = useNavigate();
  const { data: chefs } = useSuspenseQuery(chefQueries.list(60));
  const [city, setCity] = useState("");
  const [cuisine, setCuisine] = useState("");
  const [budget, setBudget] = useState("");

  const mapChefs = useMemo(() => withCoords(chefs), [chefs]);
  const hasAnyChef = chefs.length > 0;

  // Sprint 21.2 — Una sola griglia "in evidenza": Premium → Verified → recenti. Max 6, no duplicati.
  const featuredChefs = useMemo<PublicChef[]>(() => {
    const seen = new Set<string>();
    const out: PublicChef[] = [];
    const push = (c: PublicChef) => {
      if (out.length >= 6 || seen.has(c.user_id)) return;
      seen.add(c.user_id);
      out.push(c);
    };
    chefs.filter((c) => c.is_premium).forEach(push);
    chefs.filter((c) => c.verified_badge).forEach(push);
    chefs.forEach(push);
    return out;
  }, [chefs]);

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    navigate({
      to: "/search",
      search: {
        city: city || undefined,
        cuisine: cuisine || undefined,
        budget: budget ? Number(budget) : undefined,
      },
    });
  };

  return (
    <>
      {/* 1. HERO — titolo, sottotitolo, ricerca, CTA, Founder counter. */}
      <section className="relative isolate overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={heroImg} alt="" width={1600} height={1024} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/85 to-background/40" />
          <div className="absolute inset-0 bg-gradient-to-r from-background/80 via-background/30 to-transparent" />
        </div>
        <div className="container mx-auto max-w-7xl px-4 pt-20 pb-24 sm:px-6 sm:pt-28 sm:pb-32">
          <div className="max-w-2xl">
            <LaunchCounter />
            <h1 className="mt-6 font-display text-5xl leading-[1.05] sm:text-6xl md:text-7xl">
              Trova uno chef<br />
              <span className="text-gold italic">nella tua città.</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg text-muted-foreground">
              Personal chef e cuochi a domicilio in tutta Italia. Profili verificati, contatto diretto.
            </p>
          </div>

          <form onSubmit={onSearch} className="mt-10 grid gap-3 rounded-xl border border-border bg-card/95 p-3 shadow-2xl backdrop-blur-xl sm:grid-cols-[1.2fr_1.2fr_0.8fr_auto]">
            <label className="flex items-center gap-2 rounded-lg px-3 py-2.5 ring-1 ring-transparent transition focus-within:ring-gold">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <Input aria-label="Città" value={city} onChange={(e) => setCity(e.target.value)} placeholder="Città (es. Milano)" className="border-0 bg-transparent p-0 shadow-none focus-visible:ring-0" />
            </label>
            <label className="flex items-center gap-2 rounded-lg px-3 py-2.5 ring-1 ring-transparent transition focus-within:ring-gold">
              <Search className="h-4 w-4 text-muted-foreground" />
              <CuisineComboboxSingle
                value={cuisine}
                onChange={setCuisine}
                placeholder="Tipo di cucina"
                ariaLabel="Tipo di cucina"
              />
            </label>
            <label className="flex items-center gap-2 rounded-lg px-3 py-2.5 ring-1 ring-transparent transition focus-within:ring-gold">
              <Wallet className="h-4 w-4 text-muted-foreground" />
              <Input aria-label="Budget in euro" type="number" inputMode="numeric" min={0} value={budget} onChange={(e) => setBudget(e.target.value)} placeholder="Budget €" className="border-0 bg-transparent p-0 shadow-none focus-visible:ring-0" />
            </label>
            <Button type="submit" size="lg" className="bg-foreground text-background hover:bg-foreground/90">
              Cerca uno chef<ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </form>
        </div>
      </section>

      {/* 2. ANTEPRIMA MAPPA — preview cliccabile che porta a /mappa. */}
      <section aria-label="Anteprima mappa chef" className="border-y border-border bg-background">
        <div className="container mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-14">
          <Link
            to="/mappa"
            aria-label="Esplora gli chef vicino a te: apri la mappa completa"
            className="group relative block overflow-hidden rounded-2xl border border-border shadow-xl outline-none transition-transform duration-200 hover:scale-[1.01] focus-visible:ring-2 focus-visible:ring-gold cursor-pointer"
          >
            <div className="pointer-events-none h-[280px] sm:h-[380px] md:h-[440px] select-none">
              <ChefMapLazy chefs={mapChefs} height="100%" />
            </div>
            <div className="pointer-events-none absolute inset-0 flex items-end justify-center bg-gradient-to-t from-background/70 via-background/10 to-transparent p-4 sm:p-6 transition-colors duration-200 group-hover:from-background/85">
              <div className="max-w-md rounded-xl border border-border/60 bg-card/85 px-5 py-4 text-center shadow-lg backdrop-blur-md transition-colors duration-200 group-hover:bg-card/95">
                <p className="text-xs font-medium uppercase tracking-[0.2em] text-gold">📍 Mappa</p>
                <h2 className="mt-1 font-display text-xl sm:text-2xl">Esplora gli chef vicino a te</h2>
                <p className="mt-1 text-xs text-muted-foreground sm:text-sm">
                  Clicca sulla mappa per visualizzare tutti gli chef disponibili nella tua zona.
                </p>
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* 2.5 PERCHÉ — fiducia, 4 card aerate */}
      <section className="container mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-24">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-gold">Perché ChefConnect</p>
          <h2 className="mt-2 font-display text-4xl sm:text-5xl">Perché scegliere uno chef a domicilio?</h2>
        </div>
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { icon: Utensils, emoji: "🍽️", t: "Esperienza personalizzata", d: "Il menù viene creato su misura per te." },
            { icon: HomeIcon, emoji: "🏡", t: "Vivi il ristorante a casa tua", d: "Niente code, niente rumore, solo i tuoi ospiti." },
            { icon: ShieldCheck, emoji: "👨‍🍳", t: "Professionisti verificati", d: "Solo gli chef approvati possono pubblicare annunci." },
            { icon: MessageCircle, emoji: "⭐", t: "Contatto diretto", d: "Parli direttamente con lo chef, senza intermediari." },
          ].map((b) => (
            <div key={b.t} className="flex flex-col items-start gap-4 rounded-2xl border border-border bg-card p-7">
              <span aria-hidden="true" className="grid h-12 w-12 place-items-center rounded-full bg-gold-muted/40 text-2xl">
                {b.emoji}
              </span>
              <h3 className="font-display text-xl leading-tight">{b.t}</h3>
              <p className="text-sm text-muted-foreground">{b.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 3. CHEF IN EVIDENZA — una sola griglia, max 6. */}
      {hasAnyChef && (
        <section className="container mx-auto max-w-7xl px-4 py-24 sm:px-6 sm:py-28">
          <div className="flex items-end justify-between gap-6">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-gold">⭐ Chef in evidenza</p>
              <h2 className="mt-2 font-display text-4xl sm:text-5xl">I migliori, vicino a te</h2>
            </div>
            <Link to="/search" className="hidden text-sm font-medium text-muted-foreground transition-colors hover:text-foreground sm:inline-flex sm:items-center sm:gap-1">
              Vedi tutti <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {featuredChefs.map((chef) => (<ChefCard key={chef.user_id} chef={chef} />))}
          </div>
          <div className="mt-14 text-center">
            <Button asChild variant="outline" size="lg">
              <Link to="/search">Cerca uno chef <ArrowRight className="ml-1 h-4 w-4" /></Link>
            </Button>
          </div>
        </section>
      )}

      {/* 4. COME FUNZIONA — 3 card brevi: Cliente, Chef, Contatto. */}
      <section id="come-funziona" className="border-y border-border bg-foreground text-background">
        <div className="container mx-auto max-w-7xl px-4 py-24 sm:px-6 sm:py-28">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-gold">Come funziona</p>
            <h2 className="mt-2 font-display text-4xl sm:text-5xl">Semplice, in tre passi.</h2>
          </div>
          <div className="mt-16 grid gap-10 md:grid-cols-3">
            {[
              { icon: UserRound, t: "Cliente", d: "Cerca lo chef per città, cucina e budget." },
              { icon: Search, t: "Chef", d: "Profili verificati dal nostro team." },
              { icon: MessageSquare, t: "Contatto", d: "Scrivi allo chef e ricevi un preventivo." },
            ].map((step) => (
              <div key={step.t} className="flex flex-col items-start gap-4">
                <span className="grid h-12 w-12 place-items-center rounded-full bg-gold text-gold-foreground">
                  <step.icon className="h-5 w-5" />
                </span>
                <h3 className="font-display text-2xl">{step.t}</h3>
                <p className="text-sm text-background/70">{step.d}</p>
              </div>
            ))}
          </div>
          <div className="mt-14 text-center">
            <Button asChild variant="link" className="text-gold">
              <Link to="/come-funziona">Vedi i dettagli <ArrowRight className="ml-1 h-4 w-4" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* 5. PREMIUM — 3 piani, copy ridotto. */}
      <section className="border-b border-border bg-secondary/40">
        <div className="container mx-auto max-w-7xl px-4 py-24 sm:px-6 sm:py-28">
          <div className="text-center">
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-gold">Sei uno chef?</p>
            <h2 className="mt-2 font-display text-4xl sm:text-5xl">Fatti trovare dai clienti giusti.</h2>
            <p className="mx-auto mt-4 max-w-xl text-sm text-muted-foreground">
              Profilo gratis, 0 commissioni sulle serate. I primi 100 chef ricevono 30 giorni di Premium
              e il badge Founder permanente.
            </p>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            <PlanCard
              tone="free"
              name="Gratuito"
              price="0€"
              priceNote="per sempre"
              features={["Profilo pubblico", "Ricezione richieste"]}
            />
            <PlanCard
              tone="silver"
              name="Silver"
              price="€9,99"
              priceNote="al mese"
              features={["Più visibilità nei risultati", "WhatsApp con un tap", "Più foto sul profilo"]}
            />
            <PlanCard
              tone="gold"
              name="Gold"
              price="€19,99"
              priceNote="al mese"
              features={["Massima visibilità sui risultati", "Profilo in homepage", "Galleria estesa e WhatsApp"]}
              highlight
            />
          </div>

          <div className="mt-8 rounded-2xl border border-gold/30 bg-gold/10 p-6">
            <div className="flex items-start gap-4">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-gold/20 text-gold">
                <Check className="h-5 w-5" />
              </span>
              <div>
                <h3 className="font-display text-lg">Nessuna Partita IVA richiesta</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Puoi registrarti e iniziare il percorso di verifica anche se non hai una Partita IVA. Sarai tu a gestire i tuoi eventuali obblighi fiscali secondo la normativa applicabile.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-10 flex flex-col items-center gap-3 text-center">
            <Button asChild size="lg" className="bg-gold text-gold-foreground hover:bg-gold/90">
              <Link to="/diventa-chef">Diventa Chef</Link>
            </Button>
            <Link to="/premium" className="text-xs text-muted-foreground underline-offset-4 hover:underline">
              Scopri come funziona Premium
            </Link>
          </div>
        </div>
      </section>

      {/* 6. FAQ — max 4 domande. */}
      <section className="container mx-auto max-w-3xl px-4 py-24 sm:py-28">
        <div className="text-center">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-gold">Domande frequenti</p>
          <h2 className="mt-2 font-display text-4xl sm:text-5xl">Risposte rapide.</h2>
        </div>
        <Accordion type="single" collapsible className="mt-10">
          {HOME_FAQS.map((f, i) => (
            <AccordionItem key={i} value={`q${i}`}>
              <AccordionTrigger className="text-left font-medium">{f.q}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        <div className="mt-10 text-center">
          <Button asChild variant="outline"><Link to="/contatti">Hai un'altra domanda?</Link></Button>
        </div>
      </section>
    </>
  );
}

function PlanCard({
  tone, name, price, priceNote, features, highlight,
}: {
  tone: "free" | "silver" | "gold";
  name: string; price: string; priceNote: string;
  features: string[]; highlight?: boolean;
}) {
  const ring = highlight ? "ring-2 ring-gold shadow-xl" : "";
  const accent = tone === "gold" ? "text-gold" : tone === "silver" ? "text-foreground" : "text-foreground/70";
  return (
    <div className={`relative flex flex-col rounded-2xl border border-border bg-background p-8 ${ring}`}>
      {highlight && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-gold px-3 py-0.5 text-[10px] font-bold uppercase tracking-wider text-gold-foreground">
          Più scelto
        </span>
      )}
      <h3 className={`font-display text-2xl ${accent}`}>{name}</h3>
      <div className="mt-4 flex items-baseline gap-2">
        <span className="font-display text-3xl">{price}</span>
        <span className="text-xs text-muted-foreground">{priceNote}</span>
      </div>
      <ul className="mt-6 space-y-2.5 text-sm">
        {features.map((f) => (
          <li key={f} className="flex gap-2"><Heart className="mt-0.5 h-4 w-4 shrink-0 text-gold" />{f}</li>
        ))}
      </ul>
    </div>
  );
}

const HOME_FAQS: { q: string; a: string }[] = [
  { q: "Devo essere uno chef professionista per iscrivermi?", a: "No. ChefConnect è aperto a personal chef, cuochi, professionisti della ristorazione e appassionati con esperienza." },
  { q: "Quanto costa per i clienti?", a: "Cercare e contattare uno chef è gratuito. Paghi solo il servizio concordato direttamente con lui, senza intermediari." },
  { q: "Come funziona l'offerta Founder?", a: "I primi 100 chef iscritti ricevono 30 giorni di Premium Silver gratis e il badge Founder permanente sul profilo. Nessuna carta richiesta." },
  { q: "Gli chef sono verificati?", a: "Sì. Documenti d'identità, attestati HACCP e diplomi vengono controllati dal nostro team prima di assegnare il badge Verificato. La Partita IVA non è obbligatoria per iniziare." },
];
